import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { ApiContext } from '@api/api.tsx';
import { LovWithoutIdDTO } from '@api/ringil-api.v2.tsx';
import { ContainerInfo, ContainerInfoForm } from '@components/obsolete/ContainerInfo/ContainerInfo.tsx';
import SvgContainer from '@icons/Container.tsx';
import SvgNumber from '@icons/Number.tsx';

interface ComponentProps {
  secured?: SecuredConfig;
  field: 'id' | 'type';
  value?: ContainerInfoForm;
  onChange: (value: ContainerInfoForm) => void;
  readOnly?: boolean;
  required?: boolean;
  errorMessage?: string;
  dataTestId?: string;
}

// Two chips (ID + type) each open the same combined editor so the both-or-neither rule is satisfied in one save.
const ContainerInfoTag: React.FC<ComponentProps> = ({ secured, field, value, onChange, readOnly, required, errorMessage, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const { lovContainerTypes } = useContext(ApiContext);
  const [containerTypes, setContainerTypes] = useState<LovWithoutIdDTO[]>([]);
  const [internalValue, setInternalValue] = useState<ContainerInfoForm>(value);

  useEffect(() => {
    if (field === 'type') lovContainerTypes(d => setContainerTypes(d));
  }, []);

  useEffect(() => setInternalValue(value), [value]);

  const label = field === 'id' ? tr('SupplierNotificationDetail.containerId', 'Container ID') : tr('ContainerTypeTag.label', 'Container type');
  const displayValue = field === 'id' ? value?.containerId : containerTypes.find(c => c.code === value?.containerInfoCode)?.name;

  return (
    <ButtonTag
      secured={secured}
      label={label}
      icon={field === 'id' ? <SvgNumber /> : <SvgContainer />}
      value={displayValue}
      required={required}
      readonly={readOnly}
      errorMessage={errorMessage}
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      popupWidth={'400px'}
      autoClose
      dataTestId={dataTestId}
    >
      <ContainerInfo
        containerInfo={internalValue}
        onChange={v => setInternalValue(v)}
        clearable={true}
        onClear={() => setInternalValue(null)}
        inSidebar={true}
        validationErrors={[]}
        disabled={readOnly}
      />
    </ButtonTag>
  );
};

export default ContainerInfoTag;
